/** Mirrors FitnessChallengeTemplates on the server. */

export function formatDateInput(date) {
	const y = date.getFullYear();
	const m = String(date.getMonth() + 1).padStart(2, '0');
	const d = String(date.getDate()).padStart(2, '0');
	return `${y}-${m}-${d}`;
}

export function todayDateInput() {
	return formatDateInput(new Date());
}

export function tomorrowDateInput() {
	const d = new Date();
	d.setDate(d.getDate() + 1);
	return formatDateInput(d);
}

export function addDaysToDateInput(isoDate, days) {
	const d = new Date(`${isoDate}T12:00:00`);
	d.setDate(d.getDate() + days - 1);
	return formatDateInput(d);
}

export const MAX_CHALLENGE_DURATION_DAYS = 90;

export function maxChallengeEndDate(startIso) {
	if (!startIso) return '';
	return addDaysToDateInput(startIso, MAX_CHALLENGE_DURATION_DAYS);
}

export function clampChallengeEndDate(startIso, endIso) {
	if (!startIso || !endIso) return endIso;
	const maxEnd = maxChallengeEndDate(startIso);
	return endIso > maxEnd ? maxEnd : endIso;
}

export function clampChallengeDurationDays(days) {
	const n = Math.max(1, Math.round(Number(days) || 1));
	return Math.min(n, MAX_CHALLENGE_DURATION_DAYS);
}

export const CHALLENGE_DURATION_OPTIONS = [
	...Array.from({ length: 7 }, (_, i) => {
		const days = i + 1;
		return {
			value: String(days),
			label: days === 1 ? '1 day' : `${days} days`,
			days
		};
	}),
	{ value: '14', label: '2 weeks', days: 14 },
	{ value: '21', label: '3 weeks', days: 21 },
	{ value: '30', label: '1 month', days: 30 },
	{ value: '60', label: '2 months', days: 60 },
	{ value: '90', label: '3 months', days: 90 },
	{ value: 'custom', label: 'Custom', days: null }
];

export function daysForDurationChoice(choice) {
	const option = CHALLENGE_DURATION_OPTIONS.find((o) => o.value === choice);
	return option?.days ?? null;
}

export function daysBetweenInclusive(startIso, endIso) {
	if (!startIso || !endIso) return 1;
	const start = new Date(`${startIso}T12:00:00`);
	const end = new Date(`${endIso}T12:00:00`);
	const diff = Math.round((end - start) / 86400000) + 1;
	return Math.max(1, diff);
}

/** Map a day count to a preset dropdown value, or `custom`. */
export function durationChoiceFromDays(days) {
	const n = Math.max(1, Math.round(Number(days) || 1));
	const preset = CHALLENGE_DURATION_OPTIONS.find((o) => o.days === n);
	return preset ? preset.value : 'custom';
}

export function buildChallengeTitle(templateKey, params = {}) {
	const p = params;
	switch (templateKey) {
		case 'situps_count':
			return `Do ${p.count ?? 50} sit-ups`;
		case 'gym_muscle_focus':
			return `Gym day: focus on ${p.muscle_group || 'full body'}`;
		case 'water_only_today':
			return 'Drink only water';
		case 'water_oz_daily':
			return `Drink ${p.oz ?? 64} oz of water`;
		case 'vegetarian_today':
			return 'Eat vegetarian';
		case 'no_fast_food_week':
			return 'No fast food for a week';
		case 'no_fast_food_days':
			return `No fast food for ${p.days ?? 3} days`;
		case 'no_soda_week':
			return 'No soda for a week';
		case 'no_soda_days':
			return `No soda for ${p.days ?? 3} days`;
		case 'walk_minutes':
			return `Walk ${p.minutes ?? 30} minutes`;
		case 'custom':
			return '';
		default:
			return 'Fitness challenge';
	}
}

export const CUSTOM_TEMPLATE_KEY = 'custom';

export function isCustomTemplate(templateKey) {
	return templateKey === CUSTOM_TEMPLATE_KEY;
}

export const CHALLENGE_TEMPLATES = [
	{
		key: 'situps_count',
		label: 'Sit-ups',
		durationDays: 1,
		defaultParams: { count: 50 },
		fields: [{ key: 'count', label: 'How many sit-ups?', type: 'number', min: 1, max: 500, step: 1 }]
	},
	{
		key: 'walk_minutes',
		label: 'Walk',
		durationDays: 1,
		defaultParams: { minutes: 30 },
		fields: [{ key: 'minutes', label: 'How many minutes?', type: 'number', min: 5, max: 180, step: 5 }]
	},
	{
		key: 'water_only_today',
		label: 'Water only',
		durationDays: 1,
		defaultParams: {},
		fields: []
	},
	{
		key: 'water_oz_daily',
		label: 'Water goal',
		durationDays: 1,
		defaultParams: { oz: 64 },
		fields: [{ key: 'oz', label: 'Ounces per day', type: 'number', min: 32, max: 200, step: 8 }]
	},
	{
		key: 'vegetarian_today',
		label: 'Vegetarian',
		durationDays: 1,
		defaultParams: {},
		fields: []
	},
	{
		key: 'no_fast_food_days',
		label: 'No fast food',
		durationDays: 3,
		defaultParams: { days: 3 },
		fields: [{ key: 'days', label: 'How many days?', type: 'number', min: 1, max: MAX_CHALLENGE_DURATION_DAYS, step: 1 }],
		durationFromParam: 'days'
	},
	{
		key: 'no_fast_food_week',
		label: 'No fast food (week)',
		durationDays: 7,
		defaultParams: {},
		fields: []
	},
	{
		key: 'no_soda_days',
		label: 'No soda',
		durationDays: 3,
		defaultParams: { days: 3 },
		fields: [{ key: 'days', label: 'How many days?', type: 'number', min: 1, max: MAX_CHALLENGE_DURATION_DAYS, step: 1 }],
		durationFromParam: 'days'
	},
	{
		key: 'no_soda_week',
		label: 'No soda (week)',
		durationDays: 7,
		defaultParams: {},
		fields: []
	},
	{
		key: 'gym_muscle_focus',
		label: 'Gym day',
		durationDays: 1,
		defaultParams: { muscle_group: 'legs' },
		fields: [
			{
				key: 'muscle_group',
				label: 'Muscle focus',
				type: 'select',
				options: [
					{ value: 'legs', label: 'Legs' },
					{ value: 'arms', label: 'Arms' },
					{ value: 'chest', label: 'Chest' },
					{ value: 'back', label: 'Back' },
					{ value: 'full body', label: 'Full body' },
					{ value: 'cardio', label: 'Cardio' }
				]
			}
		]
	},
	{
		key: 'custom',
		label: 'Other',
		durationDays: 1,
		defaultParams: {},
		fields: [],
		isCustom: true
	}
];

export function findTemplate(key) {
	const match = CHALLENGE_TEMPLATES.find((t) => t.key === key);
	if (match) return match;
	return CHALLENGE_TEMPLATES[0];
}

export function durationDaysForTemplate(template, params) {
	if (template.durationFromParam && params[template.durationFromParam]) {
		return clampChallengeDurationDays(params[template.durationFromParam] || template.durationDays);
	}
	return template.durationDays;
}

export function buildChallengePayloadFromSuggestion(suggestion) {
	return {
		template_key: suggestion.template_key,
		custom_params: suggestion.custom_params || {},
		title: suggestion.title,
		description: suggestion.description || null
	};
}

/** @deprecated use CHALLENGE_TEMPLATES */
export const CHALLENGE_TEMPLATE_CHIPS = CHALLENGE_TEMPLATES.map((t) => ({
	key: t.key,
	label: t.label,
	defaultParams: t.defaultParams
}));
